// M5.14: The Chronicles uses ISO hour_utc only as an
// aria-label hint + analytics surface; the page itself
// reads as continuous prose (no per-paragraph time
// marker). The formatters are kept here for future
// surfaces (e.g. an analytics panel or an admin detail
// view that wants to show "this paragraph covered the
// hour starting 22:00 UTC").

import type React from 'react';

export function formatHourUtcShort(iso: string): string {
  // "2026-08-14T22:00:00Z" → "22:00 UTC"
  return iso.slice(11, 16) + ' UTC';
}

export function formatHourUtcLong(iso: string): string {
  // "2026-08-14T22:00:00Z" → "2026-08-14 22:00 UTC"
  return iso.slice(0, 10) + ' ' + iso.slice(11, 16) + ' UTC';
}

// v0.7.22: highlight actor names in a chapter paragraph.
// Replaces each occurrence of an actor name (the
// chapter's actor_names list) with a <span> that the
// chronicle page styles in a parchment-warm amber.
//
// Why server-side allow-list, not client-side name
// detection: the api already computes actor_names from
// the chapter's featured_event_ids via JOIN through
// ingestor_actor + user. Sending the list down with
// the chapter is one extra column on an existing query.
// The client doesn't have to detect Title-Case
// two-word phrases (which is fragile — "Code Review"
// and "Mount Yolo" are the same pattern as "Rick
// Tonoli" without an allow-list).
//
// Why React nodes, not string manipulation: the page
// already renders via JSX, so emitting ReactNodes from
// this helper lets the caller drop them in directly
// without HTML-parsing. The split is "longest match
// first" so "Daniel Hyles" doesn't get partially
// matched by "Daniel".
//
// Edge cases:
//   - Empty actor_names → returns the plain text in a
//     single React fragment (no spans).
//   - Name contains regex metachars → escapeRegex
//     neutralises them.
//   - Case-sensitive match: the api sends the names in
//     the same form the prose uses (capitalised first
//     letter, rest as registered). If the model writes
//     "daniel" lowercase the highlight won't fire —
//     matching model output exactly is intentional so
//     we don't false-positive on common words.
export function highlightNames(
  paragraph: string,
  actorNames: string[],
): React.ReactNode {
  if (actorNames.length === 0) {
    return paragraph;
  }
  // Longest-first so 'Daniel Hyles' wins over 'Daniel'.
  const sorted = [...actorNames].sort((a, b) => b.length - a.length);
  // Build a single regex with alternation. EscapeRegex
  // keeps names with apostrophes / hyphens safe.
  const escapeRegex = (s: string): string =>
    s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`\\b(${sorted.map(escapeRegex).join('|')})\\b`, 'g');
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let matchCount = 0;
  for (const m of paragraph.matchAll(pattern)) {
    const start = m.index ?? 0;
    if (start > lastIndex) {
      parts.push(paragraph.slice(lastIndex, start));
    }
    parts.push(
      <span key={`${start}-${m[0]}`} className="chronicle-name">
        {m[0]}
      </span>,
    );
    lastIndex = start + m[0].length;
    matchCount++;
  }
  if (lastIndex < paragraph.length) {
    parts.push(paragraph.slice(lastIndex));
  }
  // No matches at all → return the original string (caller
  // can treat it as plain text). One or more matches →
  // return the React node array. This keeps the
  // no-highlight path the cheap one (no JSX allocation).
  if (matchCount === 0) return paragraph;
  return parts;
}
